import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';

/**
 * Service that provides localStorage-like interface but uses IndexedDB internally
 * This service helps migration by maintaining the same API as localStorage
 * @author AI Assistant  
 * @date 2025-01-17
 */
@Injectable({
  providedIn: 'root'
})
export class IndexedStorageService {

  constructor(private storageService: StorageService) {}

  /**
   * Get an item from storage - returns a Promise for consistency
   * @param key - The key to retrieve
   * @returns Promise that resolves to the value or null if not found
   */
  async getItem<T = string>(key: string): Promise<T | null> {
    return await this.storageService.getItem<T>(key);
  }

  /**
   * Set an item in storage
   * @param key - The key to store the value under
   * @param value - The value to store
   * @param ttlInMs - Optional time-to-live in milliseconds
   */
  async setItem<T>(key: string, value: T, ttlInMs?: number): Promise<void> {
    await this.storageService.setItem(key, value, ttlInMs);
  }

  /**
   * Remove an item from storage
   * @param key - The key to remove
   */
  async removeItem(key: string): Promise<void> {
    await this.storageService.removeItem(key);
  }

  /**
   * Clear all items from storage
   */
  async clear(): Promise<void> {
    await this.storageService.clear();
  }

  /**
   * Get all keys from storage
   */
  async getAllKeys(): Promise<string[]> {
    return await this.storageService.getAllKeys();
  }

  /**
   * Delete expired records
   */
  async deleteExpiredRecords(): Promise<void> {
    await this.storageService.deleteExpiredRecords();
  }

  /**
   * Set an item with specific expiration time
   * @param key - The key to store the value under
   * @param value - The value to store
   * @param expirationDate - Date when the item should expire
   */
  async setItemWithExpiration<T>(key: string, value: T, expirationDate: Date): Promise<void> {
    const ttl = expirationDate.getTime() - Date.now();
    if (ttl > 0) {
      await this.setItem(key, value, ttl);
    } else {
      console.warn(`Cannot set item "${key}" with expiration in the past`);
    }
  }

  /**
   * Helper method to handle JSON parsing safely
   * @param key - The key to retrieve
   * @returns Promise that resolves to parsed JSON or null
   */
  async getJsonItem<T>(key: string): Promise<T | null> {
    try {
      const value = await this.getItem<string>(key);
      if (value === null) return null;
      
      // If it's already an object, return it
      if (typeof value === 'object') return value as T;
      
      // Try to parse as JSON
      return JSON.parse(value) as T;
    } catch (error) {
      console.warn(`Failed to parse JSON for key "${key}":`, error);
      return null;
    }
  }

  /**
   * Helper method to store JSON data
   * @param key - The key to store the value under
   * @param value - The object to store as JSON
   * @param ttlInMs - Optional time-to-live in milliseconds
   */
  async setJsonItem<T>(key: string, value: T, ttlInMs?: number): Promise<void> {
    const jsonString = JSON.stringify(value);
    await this.setItem(key, jsonString, ttlInMs);
  }

  /**
   * Batch operations for multiple keys
   * @param operations - Array of operations to perform
   */
  async batchOperations(operations: Array<{
    type: 'get' | 'set' | 'remove';
    key: string;
    value?: any;
    ttlInMs?: number;
  }>): Promise<{[key: string]: any}> {
    const results: {[key: string]: any} = {};
    
    for (const operation of operations) {
      try {
        switch (operation.type) {
          case 'get':
            results[operation.key] = await this.getItem(operation.key);
            break;
          case 'set':
            await this.setItem(operation.key, operation.value, operation.ttlInMs);
            results[operation.key] = 'success';
            break;
          case 'remove':
            await this.removeItem(operation.key);
            results[operation.key] = 'removed';
            break;
        }
      } catch (error) {
        results[operation.key] = { error: error.message };
      }
    }
    
    return results;
  }

  /**
   * Check if a key exists in storage
   * @param key - The key to check
   * @returns Promise that resolves to true if key exists
   */
  async hasKey(key: string): Promise<boolean> {
    const value = await this.getItem(key);
    return value !== null;
  }

  /**
   * Get items by key pattern (simple string matching)
   * @param pattern - Pattern to match keys against
   * @returns Promise that resolves to array of matching items
   */
  async getItemsByPattern(pattern: string): Promise<Array<{key: string, value: any}>> {
    try {
      const allKeys = await this.getAllKeys();
      const matchingKeys = allKeys.filter(key => key.includes(pattern));
      const results = [];

      for (const key of matchingKeys) {
        const value = await this.getItem(key);
        if (value !== null) {
          results.push({ key, value });
        }
      }

      return results;
    } catch (error) {
      console.error(`Error getting items by pattern "${pattern}":`, error);
      return [];
    }
  }

  /**
   * Remove items by key pattern
   * @param pattern - Pattern to match keys against
   * @returns Promise that resolves to number of items removed
   */
  async removeItemsByPattern(pattern: string): Promise<number> {
    try {
      const allKeys = await this.getAllKeys();
      const matchingKeys = allKeys.filter(key => key.includes(pattern));
      let removedCount = 0;

      for (const key of matchingKeys) {
        try {
          await this.removeItem(key);
          removedCount++;
        } catch (error) {
          console.warn(`Failed to remove key "${key}":`, error);
        }
      }

      return removedCount;
    } catch (error) {
      console.error(`Error removing items by pattern "${pattern}":`, error);
      return 0;
    }
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{total: number, expired: number, validKeys: string[]}> {
    return await this.storageService.getStorageStats();
  }

  /**
   * Export all data for backup purposes
   */
  async exportData(): Promise<{[key: string]: any}> {
    try {
      const allKeys = await this.getAllKeys();
      const exportData: {[key: string]: any} = {};

      for (const key of allKeys) {
        const value = await this.getItem(key);
        if (value !== null) {
          exportData[key] = value;
        }
      }

      return exportData;
    } catch (error) {
      console.error('Error exporting data:', error);
      return {};
    }
  }

  /**
   * Import data from backup
   * @param data - Data to import
   * @param overwrite - Whether to overwrite existing keys
   */
  async importData(data: {[key: string]: any}, overwrite: boolean = false): Promise<number> {
    let importedCount = 0;

    for (const [key, value] of Object.entries(data)) {
      try {
        if (!overwrite && await this.hasKey(key)) {
          continue; // Skip existing keys if not overwriting
        }

        await this.setItem(key, value);
        importedCount++;
      } catch (error) {
        console.warn(`Failed to import key "${key}":`, error);
      }
    }

    return importedCount;
  }
}
